import { v4 as uuidv4 } from 'uuid';
import { normalizeStoredImageItem } from './localImage.js';
import { formatVideoDuration, normalizeLocalVideoItem } from './localVideo.js';
import { getMediaItemUrl, getQiniuSignedUrl } from './qiniuMedia.js';
import { LOCAL_ASSET } from './localAssets.js';
import {
	formatPayPreviewDuration,
	getPayMediaUnlockKey,
	isPayMediaUnlocked,
	normalizePayMediaPriceFields,
	setPayMediaUnlocked,
} from './payMediaCommon.js';

export const PAY_VIDEO_PLAYER_TYPE = 'pay-video-player';
export const PAY_VIDEO_PLAYER_DEFAULT_BORDER_RADIUS = 12;
export const PAY_VIDEO_PLAYER_DEFAULT_WIDTH = 100;
// 默认视频文件的字节数（占位值，待替换为 pay-video-player-1.mp4 实际大小）
const PAY_VIDEO_PLAYER_DEFAULT_SIZE = Math.round(25 * 1024 * 1024);
const PAY_VIDEO_PLAYER_DEFAULT_SIZE_LABEL = '25 MB';
const PAY_VIDEO_PLAYER_DEFAULT_VIDEO_ID = 'dv-11001';
const UNLOCK_PREFIX = 'pay-video-player';

function createDefaultCover() {
	const preset = LOCAL_ASSET.payVideoPlayerPresets || {};
	return {
		id: `pay-video-player-cover-${uuidv4()}`,
		filename: preset.coverFilename || 'cover.jpg',
		previewUrl: preset.coverUrl || LOCAL_ASSET.payVideoPlayerCover,
		url: '',
		local: false,
	};
}

function createDefaultVideo() {
	const preset = LOCAL_ASSET.payVideoPlayerPresets || {};
	const videoUrl = preset.videoUrl || '';
	return {
		// 默认数据使用显式 id（dvp-33001），用户上传时由上传链路替换为新 id
		id: PAY_VIDEO_PLAYER_DEFAULT_VIDEO_ID,
		filename: preset.filename || '精彩视频.mp4',
		filesize: PAY_VIDEO_PLAYER_DEFAULT_SIZE_LABEL,
		filesize_byte: PAY_VIDEO_PLAYER_DEFAULT_SIZE,
		previewUrl: videoUrl,
		duration: 16,
		durationText: formatVideoDuration(16),
		url: videoUrl,
		local: false,
	};
}

function createFallback() {
	return {
		title: '暮色下的湖畔灯火',
		cover: createDefaultCover(),
		video: createDefaultVideo(),
		borderRadius: PAY_VIDEO_PLAYER_DEFAULT_BORDER_RADIUS,
		playerWidth: PAY_VIDEO_PLAYER_DEFAULT_WIDTH,
	};
}

export function getPayVideoPlayerDefaults(extra = {}) {
	const preset = LOCAL_ASSET.payVideoPlayerPresets || {};
	const videoUrl = preset.videoUrl || '';
	return normalizePayVideoPlayerData({
		cover: {
			id: `pay-video-player-cover-${uuidv4()}`,
			filename: preset.coverFilename || 'cover.jpg',
			previewUrl: preset.coverUrl || LOCAL_ASSET.payVideoPlayerCover,
			url: '',
			local: false,
		},
		video: {
			id: PAY_VIDEO_PLAYER_DEFAULT_VIDEO_ID,
			filename: preset.filename || '精彩视频.mp4',
			filesize: PAY_VIDEO_PLAYER_DEFAULT_SIZE_LABEL,
			filesize_byte: PAY_VIDEO_PLAYER_DEFAULT_SIZE,
			previewUrl: videoUrl,
			duration: 16,
			durationText: '00:16',
			url: videoUrl,
			local: false,
		},
		freePreviewDuration: 10,
		...extra,
	});
}

export function getPayVideoPlayerPreviewDefaults() {
	return getPayVideoPlayerDefaults();
}

export function normalizePayVideoPlayerData(data = {}) {
	const fallback = createFallback();
	const width = Number(data.playerWidth);
	const cover = data.cover?.previewUrl || data.cover?.url
		? normalizeStoredImageItem(data.cover, fallback.cover)
		: fallback.cover;
	const video = normalizeLocalVideoItem({ ...fallback.video, ...data.video });
	const payFields = normalizePayMediaPriceFields(data, {
		defaultButtonText: '立即解锁观看',
		maxPreviewDuration: video.duration || 9999,
	});

	return {
		title: typeof data.title === 'string' ? data.title : '暮色下的湖畔灯火',
		cover,
		video,
		borderRadius: Number.isFinite(Number(data.borderRadius))
			? Math.max(0, Number(data.borderRadius))
			: PAY_VIDEO_PLAYER_DEFAULT_BORDER_RADIUS,
		playerWidth: Number.isFinite(width) && width > 0
			? Math.min(100, Math.max(20, width))
			: PAY_VIDEO_PLAYER_DEFAULT_WIDTH,
		...payFields,
		freePreviewDuration: video.duration
			? Math.min(payFields.freePreviewDuration, video.duration)
			: payFields.freePreviewDuration,
	};
}

export function isPayVideoPlayerBlock(type) {
	return type === PAY_VIDEO_PLAYER_TYPE;
}

export function getPayVideoPlayerCoverUrl(data = {}) {
	const normalized = normalizePayVideoPlayerData(data);
	const cover = normalized.cover;
	if (cover?.previewUrl || cover?.url) {
		return getMediaItemUrl(cover);
	}
	if (normalized.video) {
		const rawUrl = normalized.video.url || normalized.video.previewUrl || '';
		if (rawUrl) {
			return getQiniuSignedUrl(rawUrl, 0);
		}
	}
	return '';
}

export function getPayVideoPlayerVideoUrl(data = {}) {
	const video = normalizePayVideoPlayerData(data).video;
	return getMediaItemUrl(video);
}

export function getPayVideoPlayerBlockStyle(data = {}, { h5 = false } = {}) {
	const normalized = normalizePayVideoPlayerData(data);
	const pxRatio = h5 ? 50 : 1;
	return {
		borderRadius: h5 ? `${normalized.borderRadius / pxRatio}rem` : `${normalized.borderRadius}px`,
		playerWidth: `${normalized.playerWidth}%`,
	};
}

export function getPayVideoPlayerUnlockKey(blockId) {
	return getPayMediaUnlockKey(blockId, UNLOCK_PREFIX);
}

export function isPayVideoPlayerUnlocked(blockId) {
	return isPayMediaUnlocked(blockId, UNLOCK_PREFIX);
}

export function setPayVideoPlayerUnlocked(blockId, unlocked = true) {
	setPayMediaUnlocked(blockId, UNLOCK_PREFIX, unlocked);
}

export function getPayVideoPlayerPreviewHint(data = {}) {
	const normalized = normalizePayVideoPlayerData(data);
	return `可免费试看前 ${formatPayPreviewDuration(normalized.freePreviewDuration)}，解锁后可看完整视频`;
}
